import { Button, Dialog, Loading, MultiStepForm, useCountries, useCurrency } from 'common';
import { BuyerSignupFormSchema } from './BuyerSignUp.schema';
import { getMaxTrialEndDate } from './BuyerSignUp.utils';
import {
    useRegisterBuyerCompanyUserProfile,
    extractAPIErrorMessage,
} from '../../services/Company/useRegisterBuyerCompanyUserProfile';
import { useBuyerSignUpFormDefaults } from '../../services/Company/useBuyerSignUpFormDefaults';
import { useERPSystems } from '../../services/ERP/useERPSystems';
import { GeneralInformation } from './Steps/GeneralInformation';
import { InboundDataFeeds } from './Steps/InboundDataFeeds';
import { OutboundDataFeeds } from './Steps/OutboundDataFeeds';
import { BuyerSettings } from './Steps/BuyerSettings';
import { useHistory } from 'react-router';
import { buyerSignUpFormText } from './BuyerSignUp.constants';
import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const text = buyerSignUpFormText;

export function BuyerSignUpForm() {
    const history = useHistory();

    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const { formDefaults, viewModelDefault, isLoading, error: defaultFormError } = useBuyerSignUpFormDefaults();

    // load data here to avoid delay upon form opening
    useCountries();
    useCurrency();
    useERPSystems();

    const {
        mutate: postBuyerCompany,
        isLoading: isSubmitting,
        isError: submissionFailed,
        isSuccess: submissionSucceeded,
        data: submissionResponse,
        error: submissionError,
    } = useRegisterBuyerCompanyUserProfile();

    const errorMessage = extractAPIErrorMessage(submissionError);
    const createdBuyerName = submissionResponse?.companyName ?? '';

    function reloadPage() {
        history.go(0);
    }

    useEffect(() => {
        if (submissionSucceeded) {
            setShowSuccessModal(true);
        }
    }, [submissionSucceeded]);

    return (
        <>
            {isLoading && <Loading />}
            {!isLoading && !defaultFormError && formDefaults && (
                <>
                    {submissionSucceeded && (
                        <Dialog
                            title={`Success`}
                            content={text.submissionStatus.success(createdBuyerName)}
                            open={showSuccessModal}
                            handleOnClose={() => setShowSuccessModal(false)}
                            actions={<Button onClick={reloadPage}>{text.submissionStatus.reload}</Button>}
                        />
                    )}
                    <MultiStepForm
                        containerSx={{
                            height: '100%',
                            minHeight: 0,
                            maxWidth: '1200px',
                            margin: 'auto',
                            p: {
                                xs: 0,
                                sm: 4,
                            },
                        }}
                        schema={BuyerSignupFormSchema}
                        formAlertProps={{
                            errorMessage: submissionFailed ? `${text.submissionStatus.error}: ${errorMessage}` : null,
                            loadingMessage: null,
                            savingMessage: isSubmitting ? text.submissionStatus.loading : null,
                            successMessage: submissionSucceeded
                                ? text.submissionStatus.success(createdBuyerName)
                                : null,
                            warningMessage: null,
                        }}
                        submitHandler={(formData) => {
                            postBuyerCompany({ buyerSignupData: formData, baseViewModel: viewModelDefault! });
                        }}
                        onCancel={(reset) => {
                            reset();
                        }}
                        shouldDisableNext={(ctx) => {
                            if (!ctx.isLastStep) {
                                return false;
                            }

                            return isSubmitting || submissionSucceeded;
                        }}
                        steps={[
                            {
                                id: 1,
                                title: text.generalInformation.title,
                                component: GeneralInformation,
                                fields: ['generalInformation'],
                            },
                            {
                                id: 2,
                                title: text.inboundDataFeeds.title,
                                component: InboundDataFeeds,
                                fields: ['inboundDataFeeds'],
                            },
                            {
                                id: 3,
                                title: text.outboundDataFeeds.title,
                                component: OutboundDataFeeds,
                                fields: ['outboundDataFeeds'],
                            },
                            {
                                id: 4,
                                title: text.buyerSettings.title,
                                component: BuyerSettings,
                                fields: ['buyerSettings'],
                            },
                        ]}
                        defaultValues={{
                            generalInformation: {
                                buyerCompanyAddress: {
                                    country: formDefaults.generalInformation?.buyerCompanyAddress?.country,
                                },
                                trialEndDate: getMaxTrialEndDate(),
                                currencyType: formDefaults.generalInformation?.currencyType,
                            },
                            inboundDataFeeds: {
                                deliveryMethod: {
                                    useTransceptaSftp:
                                        formDefaults?.inboundDataFeeds?.deliveryMethod?.useTransceptaSftp,
                                    useBuyerSftp: formDefaults?.inboundDataFeeds?.deliveryMethod?.useBuyerSftp,
                                    useEmail: formDefaults?.inboundDataFeeds?.deliveryMethod?.useEmail,
                                    sftpConnection: {
                                        server: formDefaults?.inboundDataFeeds?.deliveryMethod?.sftpConnection?.server,
                                        downloadFolder:
                                            formDefaults?.inboundDataFeeds?.deliveryMethod?.sftpConnection
                                                ?.downloadFolder,
                                        uploadFolder:
                                            formDefaults?.inboundDataFeeds?.deliveryMethod?.sftpConnection
                                                ?.uploadFolder,
                                        protocol:
                                            formDefaults?.inboundDataFeeds?.deliveryMethod?.sftpConnection?.protocol,
                                        port: formDefaults?.inboundDataFeeds?.deliveryMethod?.sftpConnection?.port,
                                        timeoutInSeconds:
                                            formDefaults?.inboundDataFeeds?.deliveryMethod?.sftpConnection
                                                ?.timeoutInSeconds,
                                    },
                                },
                                infinxIntegration: {
                                    useInfinxIntegration: false,
                                    useExistingInfinxConnection: false,
                                },
                            },
                            buyerSettings: {
                                nextGenSupplier: {
                                    ocr: true,
                                    ideTemplateMatching: true,
                                    threshold: '',
                                },
                                invoices: {
                                    enable: true,
                                    poFlip: true,
                                    poCloudMatching: true,
                                    poCloudMatchingForceOneLine: true,
                                },
                                poForDelivery: {
                                    enable: true,
                                },
                                poa: {
                                    enable: true,
                                },
                                asn: {
                                    enable: true,
                                },
                                workflow: {
                                    parkingLot: false,
                                    referencePO: true,
                                    referenceNonPO: true,
                                    cloudCode: true,
                                },
                                sim: {
                                    enable: true,
                                },
                            },
                        }}
                    />
                </>
            )}
            {!isLoading && defaultFormError && (
                <Stack direction={'column'} alignItems={'center'} justifyContent={'center'}>
                    <Typography variant="body1">{text.form.errorLoading}</Typography>
                </Stack>
            )}
        </>
    );
}
